import { LoadingOutlined } from '@ant-design/icons';
import { UseMutateFunction } from '@tanstack/react-query';
import { Dropdown, Menu, Skeleton, Spin } from 'antd';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, CrossSvg, PlusSvg } from '../../../assets/icons';
import AnswerSvg from '../../../assets/icons/AnswerSvg';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import CuriBookmarksSvg from '../../../assets/icons/CuriBookmarks';
import Trash from '../../../assets/icons/Trash';
import theme from '../../../assets/theme';
import { Input } from '../../../components/_atoms/_Input';
import { Button } from '../../../components/_atoms/Button';
import ExpandingButton from '../../../components/_atoms/ExpandingButton';
import { IconWrapper } from '../../../components/_atoms/IconWrapper';
import BaseModal from '../../../components/_molecules/BaseModal';
import { HorisontalGeneratorCard } from '../../../components/_molecules/HorisontalGeneratorCard';
import { NewLandingPageFooter } from '../../../components/_molecules/LandingPageFooter';
import LiveEmptyState from '../../../components/_molecules/LiveEmptyState';
import NewAppLayout from '../../../components/layouts/NewAppLayout';
import { useEditCollection } from '../../../hooks/useEditCollection';
import { useGetAllCollections } from '../../../hooks/useGetAllCollections';
import useGetCollectedGenerators from '../../../hooks/useGetCollectedGenerators';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useRoutePrefetch from '../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useTypewriter from '../../../hooks/useTypewriter';
import { Collection } from '../../../services/backendService/types';

const CollectionsPage = () => {
  const session = useSession();

  //prefetch the collections page
  useRoutePrefetch(`/collections/[collectionName]/[collectionId]`);

  // Prefetch a single generator page
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);

  return (
    <>
      <NewAppLayout allowUnAuthenticated={false}>
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <MyCollections />
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

const CollectionsEmptyState = () => {
  return (
    <EmptyStateContainer>
      <LiveEmptyState
        maxWidth={500}
        icon={<CuriBookmarksSvg />}
        title="Collect your favorite customizable lessons!"
        description={
          'When you find a customizable lesson you like, click the bookmark, and it will be waiting here for you'
        }
      />
    </EmptyStateContainer>
  );
};

const CollectedGeneratorsSlidingGrid = ({
  title,
  generatorIds,
  collectionId,
  onRemoveGenerator,
  setShowRenameCollectionModal,
  setNewCollectionName,
  setCurrentlyEditingCollectionId,
  deleteCollection,
}: {
  title: string;
  generatorIds: string[];
  collectionId: string;
  onRemoveGenerator: (
    collectionId: string,
    generatorId: string,
    onSuccessFunction?: ((data: string) => void | undefined) | undefined,
  ) => void;
  setShowRenameCollectionModal: React.Dispatch<React.SetStateAction<boolean>>;
  setNewCollectionName: React.Dispatch<React.SetStateAction<string>>;
  setCurrentlyEditingCollectionId: React.Dispatch<React.SetStateAction<string>>;
  deleteCollection: (collectionId: string) => void;
}) => {
  const { tenantId } = useRouterMatch();
  const reversedGeneratorIds = useMemo(() => {
    // We reverse the generatorIds to show the latest added generator first
    const reversedIds = generatorIds.map(s => s).reverse();
    return reversedIds;
  }, [generatorIds]);
  const {
    collectedGenerators,
    isFetchingCollectedGenerators,
    //fetch just the first 5 generators
  } = useGetCollectedGenerators(collectionId, reversedGeneratorIds.slice(0, 5));

  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [collectedGenerators]);

  const handleRemoveGenerator = (collectionId: string, generatorId: string) => {
    onRemoveGenerator(collectionId, generatorId);
  };

  const removeSymbolsFromCollectionName = (collectionName: string) => {
    //url-encode the url when we redirect incase there are special symbols
    return encodeURIComponent(collectionName);
  };

  const menu = (
    <Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
      <Menu.Item
        key={'rename-collection'}
        onClick={() => {
          setCurrentlyEditingCollectionId(collectionId);
          setNewCollectionName(title);
          setShowRenameCollectionModal(true);
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 40,
          }}
        >
          <IconWrapper height="18px" width="18px">
            <AnswerSvg />
          </IconWrapper>
          <span
            style={{
              fontWeight: 'bold',
              paddingLeft: 10,
            }}
          >
            Rename collection
          </span>
        </div>
      </Menu.Item>
      <Menu.Item
        key={'delete-collection'}
        onClick={() => {
          deleteCollection(collectionId);
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 40,
          }}
        >
          <IconWrapper height="18px" width="18px" style={{ color: theme.colors.red }}>
            <Trash />
          </IconWrapper>
          <span
            style={{
              fontWeight: 'bold',
              paddingLeft: 10,
              color: theme.colors.red,
            }}
          >
            Delete collection
          </span>
        </div>
      </Menu.Item>
    </Menu>
  );

  if (collectedGenerators?.length === 0) {
    return (
      <DiscoveryRowContainer key={collectionId}>
        <CollectionTitleRow>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Title $fontSize={16}>{title}</Title>
            {!collectionId.includes('my-favorites') && (
              <Dropdown placement="bottomCenter" trigger={['click']} overlay={menu}>
                <IconWrapper
                  height="16px"
                  width="16px"
                  style={{ marginLeft: 10, gap: 5 }}
                >
                  <AnswerSvg />
                </IconWrapper>
              </Dropdown>
            )}
          </div>
        </CollectionTitleRow>
        <EmptyStateContainer $isGeneratorRow={true}>
          <LiveEmptyState
            maxWidth={500}
            icon={<CuriBookmarksSvg />}
            title={`No customizable lessons in this collection yet.`}
            description={
              <Link href={`/${tenantId}/home`}>
                <SyledLink>Go explore!</SyledLink>
              </Link>
            }
          />
        </EmptyStateContainer>
      </DiscoveryRowContainer>
    );
  }

  return (
    <DiscoveryRowContainer key={collectionId}>
      <CollectionTitleRow>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title $fontSize={16}>{title}</Title>
          {!collectionId.includes('my-favorites') && (
            <Dropdown placement="bottomCenter" trigger={['click']} overlay={menu}>
              <IconWrapper height="16px" width="16px" style={{ marginLeft: 10, gap: 5 }}>
                <AnswerSvg />
              </IconWrapper>
            </Dropdown>
          )}
        </div>
        <Link
          href={`/collections/${removeSymbolsFromCollectionName(title)}/${collectionId}`}
        >
          <a>See all</a>
        </Link>
      </CollectionTitleRow>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowLeftSvg />}
                notext={true}
                onClick={() => {
                  handleLeftArrowClicked();
                }}
              />
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {collectedGenerators?.map(aiGenerator => {
            return (
              <div
                key={aiGenerator.id}
                style={{ height: '100%', width: 'fit-content', position: 'relative' }}
              >
                <Link
                  href={`/generators/${aiGenerator.title}/g/${aiGenerator.id}?collectionId=${collectionId}`}
                >
                  <a
                    style={{ height: '100%', width: 'fit-content', position: 'relative' }}
                  >
                    <HorisontalGeneratorCard
                      coverImageId={aiGenerator.coverImageId}
                      firstPublished={aiGenerator.firstPublished}
                      title={aiGenerator.title || ''}
                      status={aiGenerator.status}
                      isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                      generatorPlan={aiGenerator.aiContent?.generatorPlan}
                      tenantPlan={tenantPlan}
                      description={aiGenerator.description || undefined}
                      learningStandards={aiGenerator.learningStandards}
                    />
                  </a>
                </Link>
                <ExpandingButton
                  buttonText={`Remove`}
                  handleClick={() => handleRemoveGenerator(collectionId, aiGenerator.id)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    fontSize: 13,
                    margin: 10,
                  }}
                  icon={<CrossSvg />}
                />
              </div>
            );
          })}
          {!collectedGenerators && isFetchingCollectedGenerators && (
            <Skeleton.Button active={true} />
          )}
          {!collectedGenerators && isFetchingCollectedGenerators && (
            <Skeleton.Button active={true} />
          )}
          {!collectedGenerators && isFetchingCollectedGenerators && (
            <Skeleton.Button active={true} />
          )}
        </HorisontalScrollContainer>
        {showRightArrow && collectedGenerators && collectedGenerators.length > 2 && (
          <GeneratorsRightArrowContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowRightSvg />}
                notext={true}
                onClick={() => {
                  handleRightArrowClicked();
                }}
              />
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </DiscoveryRowContainer>
  );
};

const SyledLink = styled.a`
  font-weight: bold;
  font-size: 16px;
  color: #828282;
  text-decoration: underline;

  &:hover {
    color: #828282;
    cursor: pointer;
  }
`;

const NameInput = ({
  value,
  setNewCollectionName,
}: {
  value: string;
  setNewCollectionName: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const placeholderText = [
    'Vocabulary',
    'Social Studies',
    'Fun',
    'Bell ringers',
    'Argumentative unit',
    'SEL',
    'For review',
    'Time fillers',
  ];

  const placeHolderText = useTypewriter(placeholderText, 75);
  return (
    <Input
      value={value}
      onChange={e => setNewCollectionName(e.target.value)}
      label={'Collection name'}
      placeholder={placeHolderText}
    />
  );
};

export const MakeNewCollectionModal = ({
  visible,
  onCancel,
  createNewCollection,
  isCreatingNewCollection,
  tenantId,
}: {
  visible: boolean;
  onCancel: () => void;
  createNewCollection: UseMutateFunction<
    Collection,
    unknown,
    {
      collectionName: string;
      onSuccessFunction?: (() => void | undefined) | undefined;
    },
    unknown
  >;
  isCreatingNewCollection: boolean;
  tenantId: string;
}) => {
  const metrics = useMetrics();
  const [newCollectionName, setNewCollectionName] = useState('');
  return (
    <BaseModal
      visible={visible}
      onCancel={() => {
        setNewCollectionName('');
        onCancel();
      }}
      title={'Make new collection'}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <NameInput
          value={newCollectionName}
          setNewCollectionName={setNewCollectionName}
        />

        <Button
          onClick={() => {
            metrics.logEvent('CreateCollection.Clicked', {
              newCollectionName,
              tenantId,
            });
            createNewCollection({
              collectionName: newCollectionName,
              onSuccessFunction: () => {
                setNewCollectionName('');
                onCancel();
              },
            });
          }}
          loading={isCreatingNewCollection}
        >
          Save
        </Button>
      </div>
    </BaseModal>
  );
};

const MyCollections = () => {
  const metrics = useMetrics();
  const { tenantId } = useRouterMatch();
  const { collections, isFetchingCollections } = useGetAllCollections(tenantId);
  const {
    createNewCollection,
    isCreatingNewCollection,
    removeGeneratorFromCollection,
    renameCollection,
    deleteCollection,
  } = useEditCollection(tenantId);
  const [curentlyEditingCollectionId, setCurrentlyEditingCollectionId] = useState('');
  const [showMakeNewCollectionModal, setShowMakeNewCollectionModal] = useState(false);
  const [showRenameCollectionModal, setShowRenameCollectionModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');

  const handleRemoveGenerator = (collectionId: string, generatorId: string) => {
    removeGeneratorFromCollection({ collectionId, generatorId });
  };

  const handleDeleteCollection = (collectionId: string) => {
    metrics.logEvent('Collections.DeleteCollection.clicked', {
      collectionId,
      tenantId,
    });
    deleteCollection({ collectionId });
  };
  //Always place the collection where collectionId is my-favorites first
  const sortedCollections = useMemo(() => {
    if (!collections) return [];
    const myFavorites = collections.find(collection =>
      collection.collectionId.includes('my-favorites'),
    );
    const otherCollections = collections.filter(
      collection => !collection.collectionId.includes('my-favorites'),
    );
    return myFavorites ? [myFavorites, ...otherCollections] : collections;
  }, [collections]);

  return (
    <CollectionsContainer>
      <MakeNewCollectionModal
        visible={showMakeNewCollectionModal}
        onCancel={() => {
          setShowMakeNewCollectionModal(false);
        }}
        createNewCollection={createNewCollection}
        isCreatingNewCollection={isCreatingNewCollection}
        tenantId={tenantId}
      />
      <BaseModal
        visible={showRenameCollectionModal}
        onCancel={() => {
          setNewCollectionName('');
          setShowRenameCollectionModal(false);
        }}
        title={'Rename this collection'}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <Input
            onChange={e => setNewCollectionName(e.target.value)}
            label={'Collection name'}
            placeholder={'Bell ringers'}
            value={newCollectionName}
          />
          <Button
            onClick={() => {
              metrics.logEvent('RenameCollection.Clicked', {
                newCollectionName,
                collectionId: curentlyEditingCollectionId,
                tenantId,
              });
              renameCollection({
                collectionName: newCollectionName,
                collectionId: curentlyEditingCollectionId,
                onSuccessFunction: () => {
                  setNewCollectionName('');
                  setShowRenameCollectionModal(false);
                },
              });
            }}
            loading={isCreatingNewCollection}
          >
            Save
          </Button>
        </div>
      </BaseModal>
      <TopRow>
        <h1>My collections</h1>
        <Button onClick={() => setShowMakeNewCollectionModal(true)} icon={<PlusSvg />}>
          New collection
        </Button>
      </TopRow>
      {!collections && isFetchingCollections && (
        <Spin
          indicator={
            <LoadingOutlined style={{ fontSize: 50, color: theme.colors.orange }} spin />
          }
        />
      )}
      {collections && !isFetchingCollections && (
        <>
          {collections.length === 0 && <CollectionsEmptyState />}
          {sortedCollections.length > 0 && (
            <>
              {sortedCollections.map(collection => {
                return (
                  <CollectedGeneratorsSlidingGrid
                    key={collection.collectionId}
                    title={collection.collectionName}
                    collectionId={collection.collectionId}
                    generatorIds={collection.collectedGeneratorIds}
                    onRemoveGenerator={handleRemoveGenerator}
                    setShowRenameCollectionModal={setShowRenameCollectionModal}
                    setNewCollectionName={setNewCollectionName}
                    setCurrentlyEditingCollectionId={setCurrentlyEditingCollectionId}
                    deleteCollection={handleDeleteCollection}
                  />
                );
              })}
            </>
          )}
        </>
      )}
    </CollectionsContainer>
  );
};

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const CollectionsContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 80px 20px 20px 20px;
  position: relative;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const DiscoveryRowContainer = styled.div`
  margin-bottom: 30px;

  @media (max-width: 800px) {
    margin-bottom: 30px;
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  text-transform: uppercase;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;

const GeneratorsLeftArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  left: 20px;
  top: 0;
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const GeneratorsRightArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  right: 20px;
  top: 0;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const ScrollMainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 210px;
  padding: 0px 20px;
  @media (max-width: 800px) {
    height: 150px;
  }
`;

const StyledArrowButton = styled(Button)`
  padding: 0px;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const HorisontalScrollContainer = styled.ul`
  width: 100%;
  height: 210px;
  overflow-x: auto;
  overflow-y: hidden;

  display: flex;
  gap: 10px;
  padding: 10px 0px 10px 0px; // padding bottom to contain scroll bar

  // Hide the horisontal scrollbars
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }

  .ant-skeleton,
  .ant-skeleton-button {
    height: 100%;
    aspect-ratio: 1.28;
    border-radius: 5px;
    min-width: unset;
    width: unset;
  }

  @media (max-width: 800px) {
    height: 150px;
  }
`;

const EmptyStateContainer = styled.div<{ $isGeneratorRow?: boolean }>`
  div > svg {
    height: 150px !important;
    width: 200px !important;
  }

  ${({ $isGeneratorRow }) =>
    $isGeneratorRow &&
    css`
      padding: 0px 20px;
      > div {
        margin-top: 0px;
        padding: 10px 0px;
        min-height: fit-content;
        div > svg {
          height: 70px !important;
          width: 100px !important;
        }
        p {
          margin: 0px;
        }
        h3 {
          margin-top: 5px !important;
        }
      }
    `}

  @media (max-width: 800px) {
    div > svg {
      height: 100px !important;
      width: 150px !important;
    }
  }
`;

const CollectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  h3 {
    width: fit-content;
  }

  svg {
    margin-bottom: 5px;
  }

  svg:hover {
    cursor: pointer;
    color: ${theme.colors.iconGrey};
  }

  a {
    font-weight: bold;
    font-size: 16px;
    text-decoration: underline;
  }

  a:hover {
    cursor: pointer;
    text-decoration: none;
  }
`;

export default CollectionsPage;
